class ScrollToTop {
  constructor() {
    this.button = document.getElementById("scroll-to-top");
    this.scrollThreshold = 400; // Show button after scrolling 400px
    this.isVisible = false;

    if (!this.button) return;

    this.init();
  }

  init() {
    this.bindEvents();
    this.checkScrollPosition(); // Check initial position
  }

  bindEvents() {
    // Show/hide button based on scroll position with throttling
    window.addEventListener(
      "scroll",
      this.throttle(this.handleScroll.bind(this), 100),
      { passive: true }
    );

    // Handle button click
    this.button.addEventListener("click", this.scrollToTop.bind(this));

    // Handle keyboard navigation
    this.button.addEventListener("keydown", this.handleKeydown.bind(this));

    // Handle reduced motion preference
    this.setupReducedMotion();
  }

  handleScroll() {
    this.checkScrollPosition();
  }

  handleKeydown(event) {
    // Enhanced keyboard support
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.scrollToTop();
    }
  }

  checkScrollPosition() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const shouldShow = scrollTop > this.scrollThreshold;

    if (shouldShow && !this.isVisible) {
      this.showButton();
    } else if (!shouldShow && this.isVisible) {
      this.hideButton();
    }
  }

  showButton() {
    this.isVisible = true;

    // Announce to screen readers when button becomes available
    this.button.setAttribute("aria-hidden", "false");
    this.button.setAttribute("tabindex", "0");

    // Visual transition
    requestAnimationFrame(() => {
      this.button.classList.remove(
        "opacity-0",
        "pointer-events-none",
        "translate-y-2"
      );
      this.button.classList.add("opacity-100");
    });
  }

  hideButton() {
    this.isVisible = false;

    // Remove from accessibility tree
    this.button.setAttribute("aria-hidden", "true");
    this.button.setAttribute("tabindex", "-1");

    // Remove focus if button currently has it
    if (document.activeElement === this.button) {
      this.button.blur();
    }

    // Visual transition
    this.button.classList.remove("opacity-100");
    this.button.classList.add(
      "opacity-0",
      "pointer-events-none",
      "translate-y-2"
    );
  }

  scrollToTop() {
    const scrollBehavior = this.prefersReducedMotion() ? "auto" : "smooth";

    // Announce action to screen readers
    this.announceToScreenReader("Scrolling to top of page");

    window.scrollTo({
      top: 0,
      behavior: scrollBehavior,
    });

    // Force check scroll position after scroll completes
    if (scrollBehavior === "smooth") {
      // Check multiple times during smooth scroll
      const checkInterval = setInterval(() => {
        this.checkScrollPosition();
        const currentScroll =
          window.pageYOffset || document.documentElement.scrollTop;
        if (currentScroll <= 50) {
          // Close to top
          clearInterval(checkInterval);
        }
      }, 50);

      // Fallback timeout
      setTimeout(() => {
        clearInterval(checkInterval);
        this.checkScrollPosition();
      }, 1000);
    } else {
      // For instant scroll, check immediately
      setTimeout(() => {
        this.checkScrollPosition();
      }, 0);
    }

    // Focus management
    const skipLink = document.getElementById("skip-to-content-link");
    const mainContent = document.getElementById("MainContent");

    if (skipLink) {
      setTimeout(
        () => {
          skipLink.focus();
        },
        scrollBehavior === "smooth" ? 500 : 0
      );
    } else if (mainContent) {
      setTimeout(
        () => {
          mainContent.focus();
        },
        scrollBehavior === "smooth" ? 500 : 0
      );
    }
  }

  setupReducedMotion() {
    // Respect user's motion preferences
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    const handleMotionChange = (e) => {
      if (e.matches) {
        this.button.style.transition = "opacity 0.1s ease";
      } else {
        this.button.style.transition = "";
      }
    };

    handleMotionChange(mediaQuery);
    mediaQuery.addEventListener("change", handleMotionChange);
  }

  prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  announceToScreenReader(message) {
    // Create live region for announcements
    let liveRegion = document.getElementById("scroll-to-top-announcer");

    if (!liveRegion) {
      liveRegion = document.createElement("div");
      liveRegion.id = "scroll-to-top-announcer";
      liveRegion.setAttribute("aria-live", "polite");
      liveRegion.setAttribute("aria-atomic", "true");
      liveRegion.className = "sr-only";
      document.body.appendChild(liveRegion);
    }

    liveRegion.textContent = message;

    // Clear the message after announcement
    setTimeout(() => {
      liveRegion.textContent = "";
    }, 1000);
  }

  // Throttle function to limit scroll event frequency
  throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }
}

// Initialize when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  new ScrollToTop();
});
